import { defineStore } from "pinia";
import { SettingsTemplate, type TitleReadingMode } from "~/src/api";
import { useSettingsStore } from "~/stores/settings";
import naturalEval from "~/src/naturalEval";
import { diff } from "deep-diff";
import cloneDeep from "clone-deep";
import type { WatchStopHandle } from "vue";
import { debounce } from "perfect-debounce";
import {
  getDefaultNovelSettings,
  NovelProfileMap,
  type NovelSettingsInterface,
  type NovelSettingsType,
} from "~/src/novel/reader";

const debug = (...args: any[]) => {
  console.debug(
    "%c%s",
    "color:black;background-color:#ff9999;padding:2px 4px;border-radius:4px;",
    "Novel Settings",
    ...args,
  );
};

interface SyncedNovelSettings extends NovelSettingsInterface {
  _stopHandles: WatchStopHandle[];
}

const state = (): SyncedNovelSettings => ({
  ...getDefaultNovelSettings(),

  // unsaved
  _stopHandles: [],
});

const onSettingsUpdateHooks: (() => void)[] = [];

export const useNovelSettings = defineStore("novel-reader-store", {
  state,
  actions: {
    async initialize(ctx?: {
      "title.readingMode"?: TitleReadingMode;
      "screen.width"?: number;
    }) {
      const startTime = Date.now();
      const settingsStore = useSettingsStore();
      await settingsStore.waitForInitialization();
      if (settingsStore.settings.novelReader?.length > 0)
        this.$patch({
          profiles: cloneDeep(settingsStore.settings.novelReader),
        });
      debug("Settings initialized in", Date.now() - startTime, "ms");

      if (ctx)
        this.currentProfileIndex = Math.max(
          this.profiles.findIndex(
            (prof) => !prof.condition || naturalEval(prof.condition, ctx),
          ),
          0,
        );
      this.menuOpen =
        !!this.profiles[this.currentProfileIndex]?.openMenuOnPageLoad;

      this.addWatchers();
    },
    addWatchers() {
      const debouncedUpdate = debounce(this.updateSettings, 300);

      this._stopHandles.push(
        watch(
          () => [this.profiles],
          () => setTimeout(debouncedUpdate),
          { deep: true },
        ),
      );
      this._stopHandles.push(
        watch(
          () => this.menuOpen,
          (newVal) => {
            if (this.profiles[this.currentProfileIndex])
              this.profiles[this.currentProfileIndex].openMenuOnPageLoad =
                newVal;
          },
        ),
      );
    },
    updateSettings() {
      update(this.$state);
    },
    purge() {
      while (this._stopHandles.length > 0) {
        this._stopHandles.pop()?.apply(null);
      }

      this.$reset();
    },
    onSettingsUpdate: (hook: () => void) => {
      onSettingsUpdateHooks.push(hook);
    },
    resetProfile(profile: string) {
      const profileSettings = NovelProfileMap[profile];
      const profileIndex = this.profiles.findIndex(
        (prof) => prof.profile === profile,
      );
      if (profileSettings && profileIndex >= 0) {
        this.profiles.splice(profileIndex, 1, profileSettings);
      }
    },
  },
});

let shouldNotify = true;
const ignoreSettings = new Set<keyof NovelSettingsType>(["openMenuOnPageLoad"]);

function update(readerState: ReturnType<typeof state>) {
  const settingsStore = useSettingsStore();
  const currentTemplate = new SettingsTemplate({
    data: settingsStore.settings,
  });

  const initial = cloneDeep(currentTemplate.template.data.novelReader);
  const current = cloneDeep(readerState.profiles);

  const currentProfile = readerState.profiles[readerState.currentProfileIndex];
  if (!currentProfile) return;

  const difference = diff(initial, current);
  if (difference === undefined) return;

  debug("Updating ", currentProfile.profile, difference);

  shouldNotify = difference.some(
    (diff) => diff.path && !ignoreSettings.has(diff.path[diff.path.length - 1]),
  );
  currentTemplate.template.data.novelReader = current;

  queue(async () => settingsStore.update(currentTemplate.template.data));
}

let hasInstance = false;
let stopSignal = false;
let queueArray: (() => Promise<void>)[] = [];

async function queue(fn: () => Promise<void>) {
  queueArray.push(fn);

  if (hasInstance) return;
  hasInstance = true;

  let isRunning = false;

  const timer = setInterval(async () => {
    if (stopSignal) {
      clearInterval(timer);
      return;
    }

    if (isRunning) return;

    if (queueArray.length > 0) {
      isRunning = true;
      const takenSplice = queueArray.splice(0);
      await takenSplice[takenSplice.length - 1]();
      isRunning = false;

      if (shouldNotify) {
        onSettingsUpdateHooks.forEach((hook) => hook());
      }
    }
  }, 500);
}
